import { create } from 'zustand';
import { getRoleClaimFromJwt } from '@/lib/auth/jwt';
import { MERCHANT_STAFF_ROLE, type MerchantRole } from '@/lib/auth/rbac';

export interface AuthUser {
  id: string;
  phone: string;
  name?: string;
  role: MerchantRole;
  email?: string | null;
}

export interface MerchantSummary {
  id: string;
  business_name: string;
  status: string;
}

export interface BranchSummary {
  id: string;
  name: string;
  address?: string | null;
}

function resolveActiveBranchId(
  branches: BranchSummary[],
  savedActiveBranchId: string | 'all' | null,
): string | 'all' | null {
  if (branches.length === 0) return null;

  if (branches.length === 1) {
    return branches[0]?.id ?? null;
  }

  if (savedActiveBranchId === 'all') {
    return 'all';
  }

  if (
    typeof savedActiveBranchId === 'string' &&
    branches.some((branch) => branch.id === savedActiveBranchId)
  ) {
    return savedActiveBranchId;
  }

  return 'all';
}

function normalizeAuthUserRoleFromToken(
  user: AuthUser | null,
  accessToken: string | null,
): AuthUser | null {
  if (!user) {
    return null;
  }

  const roleFromJwt = getRoleClaimFromJwt(accessToken) ?? MERCHANT_STAFF_ROLE;
  if (user.role === roleFromJwt) {
    return user;
  }

  return {
    ...user,
    role: roleFromJwt,
  };
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  merchant: MerchantSummary | null;
  branches: BranchSummary[];
  activeBranchId: string | 'all' | null;

  setSession: (s: {
    access_token: string;
    refresh_token?: string;
    user: AuthUser;
    merchant?: MerchantSummary;
    branches?: BranchSummary[];
  }) => void;
  syncBranches: (branches: BranchSummary[]) => void;
  setActiveBranch: (id: string | 'all') => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  accessToken: null,
  refreshToken: null,
  user: null,
  merchant: null,
  branches: [],
  activeBranchId: null,

  setSession: ({ access_token, refresh_token, user, merchant, branches }) =>
    set((state) => {
      const normalizedBranches = branches ?? [];
      const normalizedUser = normalizeAuthUserRoleFromToken(user, access_token);

      return {
        accessToken: access_token,
        refreshToken:
          typeof refresh_token === 'string' && refresh_token.length > 0
            ? refresh_token
            : state.refreshToken,
        user: normalizedUser,
        merchant: merchant ?? null,
        branches: normalizedBranches,
        activeBranchId: resolveActiveBranchId(normalizedBranches, state.activeBranchId),
      };
    }),
  syncBranches: (branches) =>
    set((state) => {
      const normalizedBranches = branches ?? [];

      return {
        branches: normalizedBranches,
        activeBranchId: resolveActiveBranchId(normalizedBranches, state.activeBranchId),
      };
    }),
  setActiveBranch: (id) => set({ activeBranchId: id }),
  logout: () =>
    set({
      accessToken: null,
      refreshToken: null,
      user: null,
      merchant: null,
      branches: [],
      activeBranchId: null,
    }),
}));
